import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSiteData } from "../../lib/hooks";
import { fileUrl } from "../../lib/api";

const NAV = [
  { label: "HOME", to: "/" },
  { label: "ABOUT", to: "/about" },
  { label: "OUR MENUS", to: "/menus" },
  { label: "CATERING", to: "/catering" },
  { label: "EXPERIENCES", to: "/experiences" },
  { label: "CORPORATE", to: "/corporate" },
  { label: "GALLERY", to: "/gallery" },
  { label: "CONTACT", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const settings = useSiteData();
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const solid = scrolled || !onHome;

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solid ? "glass border-b border-gold/20 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <img
              src={fileUrl(settings.logo_url || "/logo.jpeg")}
              alt="Foodie Connect Caterer"
              className={`transition-all duration-500 rounded-md object-cover ${solid ? "h-14 sm:h-16" : "h-16 sm:h-20"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`text-[0.72rem] tracking-[0.18em] font-medium transition-colors ${
                  location.pathname === n.to ? "text-gold" : "text-ivory/85 hover:text-gold"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`https://wa.me/${settings.whatsapp || "919381205545"}?text=${encodeURIComponent(settings.whatsapp_message || "Hello Foodie Connect Caterer")}`}
              target="_blank" rel="noreferrer"
              data-testid="nav-whatsapp"
              className="text-ivory/80 hover:text-gold transition-colors"
              aria-label="WhatsApp"
            >
              <i className="fa-brands fa-whatsapp text-xl" />
            </a>
            <button onClick={() => navigate("/quote-builder")} data-testid="nav-plan-event" className="btn-gold !py-3 !px-5">
              PLAN YOUR EVENT
            </button>
          </div>

          <button
            className="lg:hidden text-ivory p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            aria-label="Open menu"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-all duration-500 ${open ? "visible" : "invisible pointer-events-none"}`}
      >
        <div className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 bottom-0 w-[82%] max-w-sm bg-burgundy grain overflow-y-auto transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between p-5 border-b border-gold/20">
            <img src={fileUrl(settings.logo_url || "/logo.jpeg")} alt="logo" className="h-14 rounded" />
            <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" className="text-ivory p-2"><X className="w-6 h-6" /></button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className="text-ivory/90 font-serif text-2xl py-3 border-b border-gold/10 hover:text-gold transition-colors">
                {n.label}
              </Link>
            ))}
            <button onClick={() => navigate("/quote-builder")} className="btn-gold mt-6 justify-center">PLAN YOUR EVENT</button>
          </nav>
        </div>
      </div>
    </>
  );
}
